/**
 * subscriptionManager.ts
 *
 * Manages the AppSync WebSocket subscription for a session.
 * Uses Amplify v6's Observable .subscribe() pattern (not async iteration).
 */

import { generateClient } from 'aws-amplify/api';
import { ON_SESSION_UPDATED } from '@/lib/graphql/subscriptions';
import { useStore } from '@/store';
import { suppressNextSync } from '@/sync/sessionSync';
import { Session } from '@airline-demo/types';

// Lazy: generateClient() must be called after Amplify.configure()
let _client: { graphql: Function } | null = null;
const getClient = () => {
  if (!_client) _client = generateClient();
  return _client;
};

let activeSubscription: { unsubscribe: () => void } | null = null;

export function startSubscription(sessionId: string): () => void {
  // Clean up any existing subscription first
  if (activeSubscription) {
    activeSubscription.unsubscribe();
    activeSubscription = null;
  }

  const observable = getClient().graphql({
    query: ON_SESSION_UPDATED,
    variables: { sessionId },
  }) as unknown as {
    subscribe: (handlers: {
      next: (event: { data: { onSessionUpdated: Session } }) => void;
      error: (err: unknown) => void;
    }) => { unsubscribe: () => void };
  };

  activeSubscription = observable.subscribe({
    next: ({ data }) => {
      const updated = data?.onSessionUpdated;
      if (updated) {
        // Suppress the sync watcher so this server-pushed update
        // doesn't immediately trigger another outbound mutation
        suppressNextSync();
        useStore.getState().hydrateFromSession(updated);
      }
    },
    error: (err) => {
      // Amplify will attempt reconnection automatically
      console.warn('[subscriptionManager] subscription error:', err);
    },
  });

  return () => {
    if (activeSubscription) {
      activeSubscription.unsubscribe();
      activeSubscription = null;
    }
  };
}

export function stopSubscription() {
  if (activeSubscription) {
    activeSubscription.unsubscribe();
    activeSubscription = null;
  }
}
